import React, { useState, useEffect, useMemo } from 'react';
import DrukteKalender from './DrukteKalender';

export default function OnderhoudForecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Capaciteits-sliders
  const [monteurs, setMonteurs] = useState(1);
  const [uurPerWeek, setUurPerWeek] = useState(32);
  const [pctOnderhoud, setPctOnderhoud] = useState(60);

  // Belastings-sliders
  const [storingenPerWeek, setStoringenPerWeek] = useState(0);
  const [urenPerStoring, setUrenPerStoring] = useState(2);
  const [urenPerOnderhoud, setUrenPerOnderhoud] = useState(1.5);
  const [nogInTePlannen, setNogInTePlannen] = useState(0);
  const [gebruikKlanten, setGebruikKlanten] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/onderhoud/forecast', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setData(j.data);
          if (j.data.aantal_monteurs) setMonteurs(j.data.aantal_monteurs);
          if (j.data.gemiddelde_uren_per_onderhoud > 0) {
            setUrenPerOnderhoud(j.data.gemiddelde_uren_per_onderhoud);
          }
        } else setError(j.message || 'Fout bij laden');
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const calc = useMemo(() => {
    if (!data) return null;
    const { doel_totaal, afgerond, dagen_verstreken, dagen_totaal } = data;
    const resterende_dagen = Math.max(0, dagen_totaal - dagen_verstreken);
    const resterende_weken = resterende_dagen / 7;
    const te_gaan_ingepland = Math.max(0, doel_totaal - afgerond);
    const extra_onderhouden = gebruikKlanten ? (data.klanten_zonder_onderhoud || 0) : nogInTePlannen;
    const te_gaan = te_gaan_ingepland + extra_onderhouden;
    const effectief_doel = doel_totaal + extra_onderhouden;

    // Capaciteit
    const beschikbare_uren = monteurs * uurPerWeek * (pctOnderhoud / 100) * resterende_weken;
    const storings_uren = storingenPerWeek * urenPerStoring * resterende_weken;
    const nuttige_uren = Math.max(0, beschikbare_uren - storings_uren);
    const haalbaar_raw = nuttige_uren / Math.max(urenPerOnderhoud, 0.25);
    const haalbaar_extra = Math.min(haalbaar_raw, te_gaan);

    // Prognose
    const prognose = afgerond + haalbaar_extra;
    const verschil = prognose - effectief_doel;

    // Ook: huidige pace prognose (ter vergelijking, op basis van YTD)
    const pace_per_dag = afgerond / Math.max(1, dagen_verstreken);
    const prognose_op_pace = Math.round(pace_per_dag * dagen_totaal * 10) / 10;
    const op_schema_nu = Math.round((dagen_verstreken / dagen_totaal) * effectief_doel * 10) / 10;

    let status = 'op schema', kleur = '#10b981';
    if (verschil < -0.5) { status = 'achter'; kleur = '#ef4444'; }
    else if (verschil > 0.5) { status = 'voor'; kleur = '#3b82f6'; }

    return {
      beschikbare_uren, storings_uren, nuttige_uren, haalbaar_raw, haalbaar_extra,
      prognose, verschil, status, kleur, prognose_op_pace, op_schema_nu, te_gaan, te_gaan_ingepland, effectief_doel,
      capaciteit_tekort: haalbaar_raw < te_gaan
    };
  }, [data, monteurs, uurPerWeek, pctOnderhoud, storingenPerWeek, urenPerStoring, urenPerOnderhoud, nogInTePlannen, gebruikKlanten]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Laden…</div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444' }}>Fout: {error}</div>;
  if (!data || !calc) return null;
  if (data.doel_totaal === 0) return <div style={{ padding: 40 }}>Nog geen actieve onderhoudsregels.</div>;

  // Grafiek
  const huidigeMaand = new Date().getMonth() + 1;
  let cum = 0;
  const werkelijk = data.afgerond_per_maand.map(m => {
    cum += m.aantal;
    return { maand: m.maand, y: m.maand <= huidigeMaand ? cum : null };
  });
  const doelLijn = Array.from({ length: 12 }, (_, i) => ({
    maand: i + 1, y: Math.round(((i + 1) / 12) * data.doel_totaal * 10) / 10
  }));
  const prognoseLijn = Array.from({ length: 12 }, (_, i) => {
    const maand = i + 1;
    if (maand < huidigeMaand) return { maand, y: null };
    if (maand === huidigeMaand) return { maand, y: data.afgerond };
    const restFraction = (maand - huidigeMaand) / (12 - huidigeMaand || 1);
    return { maand, y: Math.round((data.afgerond + calc.haalbaar_extra * restFraction) * 10) / 10 };
  });

  const W = 720, H = 300, PAD_L = 50, PAD_R = 20, PAD_T = 20, PAD_B = 40;
  const maxY = Math.max(data.doel_totaal, calc.prognose, data.afgerond) * 1.15 || 10;
  const xFor = m => PAD_L + ((m - 1) / 11) * (W - PAD_L - PAD_R);
  const yFor = v => PAD_T + (1 - v / maxY) * (H - PAD_T - PAD_B);
  const pathFor = (arr) => {
    const pts = arr.filter(p => p.y !== null);
    if (pts.length === 0) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(p.maand)} ${yFor(p.y)}`).join(' ');
  };
  const maanden = ['Jan','Feb','Mrt','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];

  const card = { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
  const stat = { ...card, textAlign: 'center' };
  const label = { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
  const bigNum = { fontSize: 32, fontWeight: 700, color: '#1e293b' };

  const Slider = ({ titel, waarde, setter, min, max, step, suffix, hint }) => (
    <div>
      <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
        <span>{titel}</span>
        <span style={{ color: '#3b82f6' }}>{waarde}{suffix || ''}</span>
      </label>
      <input type="range" min={min} max={max} step={step} value={waarde}
        onChange={e => setter(parseFloat(e.target.value))}
        style={{ width: '100%', marginTop: 6 }} />
      {hint && <div style={{ fontSize: 11, color: '#94a3b8' }}>{hint}</div>}
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: '-apple-system, system-ui, sans-serif' }}>
      <h1 style={{ fontSize: 26, marginBottom: 4 }}>📊 Onderhoud Forecast {data.jaar}</h1>
      <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>
        Dag {data.dagen_verstreken} van {data.dagen_totaal} · {calc.te_gaan} onderhouden te gaan · {Math.round((data.dagen_totaal - data.dagen_verstreken) / 7)} weken resterend
      </p>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={stat}><div style={label}>Doel</div><div style={bigNum}>{data.doel_totaal}</div></div>
        <div style={stat}>
          <div style={label}>Afgerond</div>
          <div style={{ ...bigNum, color: '#10b981' }}>{data.afgerond}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>op schema: {calc.op_schema_nu}</div>
        </div>
        <div style={stat}>
          <div style={label}>Prognose eind jaar</div>
          <div style={{ ...bigNum, color: calc.kleur }}>{Math.round(calc.prognose * 10) / 10}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            {calc.verschil >= 0 ? '+' : ''}{calc.verschil.toFixed(1)} t.o.v. doel
          </div>
        </div>
        <div style={stat}>
          <div style={label}>Status</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: calc.kleur, marginTop: 8 }}>
            {calc.status === 'voor' && '🚀 '}{calc.status === 'op schema' && '✅ '}{calc.status === 'achter' && '⚠️ '}
            {calc.status}
          </div>
        </div>
      </div>

      {/* Capaciteit cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={stat}>
          <div style={label}>Beschikbare uren</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>{Math.round(calc.beschikbare_uren)}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>resterend jaar, voor onderhoud</div>
        </div>
        <div style={stat}>
          <div style={label}>Verloren aan storingen</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: storingenPerWeek > 0 ? '#ef4444' : '#94a3b8' }}>
            −{Math.round(calc.storings_uren)}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>bij {storingenPerWeek}/week × {urenPerStoring}u</div>
        </div>
        <div style={stat}>
          <div style={label}>Nuttig voor onderhoud</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{Math.round(calc.nuttige_uren)}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>≈ {Math.round(calc.haalbaar_raw * 10) / 10} onderhouden</div>
        </div>
      </div>

      {/* Sliders */}
      <div style={{ ...card, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Capaciteit</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>Hoeveel tijd heeft je team beschikbaar?</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          <Slider titel="Monteurs" waarde={monteurs} setter={setMonteurs} min="1" max="20" step="1"
            hint={`detectie uit DB: ${data.aantal_monteurs || 'onbekend'}`} />
          <Slider titel="Uur per monteur / week" waarde={uurPerWeek} setter={setUurPerWeek} min="16" max="40" step="1"
            suffix="u" hint="netto na vakantie, admin etc." />
          <Slider titel="% tijd voor onderhoud" waarde={pctOnderhoud} setter={setPctOnderhoud} min="10" max="100" step="5"
            suffix="%" hint="rest = projecten, reizen, overleg" />
        </div>

        <h3 style={{ marginTop: 24, marginBottom: 6 }}>Belasting</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>Wat kost het?</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          <Slider titel="Storingen per week" waarde={storingenPerWeek} setter={setStoringenPerWeek} min="0" max="15" step="0.5"
            hint={`YTD werkelijk: ${data.storingen_ytd}`} />
          <Slider titel="Uur per storing" waarde={urenPerStoring} setter={setUrenPerStoring} min="0.5" max="8" step="0.5"
            suffix="u" hint="gemiddeld" />
          <Slider titel="Uur per onderhoud" waarde={urenPerOnderhoud} setter={setUrenPerOnderhoud} min="0.5" max="8" step="0.25"
            suffix="u" hint="gemiddeld per beurt" />
        </div>

        <h3 style={{ marginTop: 24, marginBottom: 6 }}>Nog in te plannen</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          Hoeveel onderhoud komt er nog bij dit jaar dat nu niet in het systeem staat?
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={gebruikKlanten} onChange={e => setGebruikKlanten(e.target.checked)} />
          <span>
            Gebruik klanten zonder onderhoud-record (<strong>{data.klanten_zonder_onderhoud || 0}</strong> gevonden in database)
          </span>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, opacity: gebruikKlanten ? 0.5 : 1, pointerEvents: gebruikKlanten ? 'none' : 'auto' }}>
          <Slider titel="Extra onderhouden (handmatig)" waarde={nogInTePlannen} setter={setNogInTePlannen} min="0" max="500" step="5"
            hint={gebruikKlanten ? 'uitgeschakeld — checkbox staat aan' : `totaal wordt dan ${data.doel_totaal + nogInTePlannen}`} />
          <div style={{ fontSize: 13, color: '#64748b', alignSelf: 'center', opacity: 1, pointerEvents: 'auto' }}>
            Effectief doel: <strong style={{ color: '#1e293b' }}>{calc.effectief_doel}</strong><br/>
            Nog te doen: <strong style={{ color: '#1e293b' }}>{calc.te_gaan}</strong>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', alignSelf: 'center', opacity: 1, pointerEvents: 'auto' }}>
            Totaal uren nodig: <strong style={{ color: '#1e293b' }}>{Math.round(calc.te_gaan * urenPerOnderhoud)}u</strong><br/>
            Beschikbaar: <strong style={{ color: calc.nuttige_uren >= calc.te_gaan * urenPerOnderhoud ? '#10b981' : '#ef4444' }}>
              {Math.round(calc.nuttige_uren)}u
            </strong>
          </div>
        </div>
      </div>

      {/* Grafiek */}
      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Cumulatief verloop</h3>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
          {[0, 0.25, 0.5, 0.75, 1].map(f => {
            const y = PAD_T + f * (H - PAD_T - PAD_B);
            return (
              <g key={f}>
                <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={PAD_L - 8} y={y + 4} fontSize="11" fill="#94a3b8" textAnchor="end">
                  {Math.round(maxY * (1 - f))}
                </text>
              </g>
            );
          })}
          {maanden.map((m, i) => (
            <text key={i} x={xFor(i + 1)} y={H - PAD_B + 18} fontSize="11" fill="#64748b" textAnchor="middle">{m}</text>
          ))}
          <line x1={xFor(huidigeMaand)} y1={PAD_T} x2={xFor(huidigeMaand)} y2={H - PAD_B}
            stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
          <path d={pathFor(doelLijn)} fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="4 4" />
          <path d={pathFor(werkelijk)} fill="none" stroke="#10b981" strokeWidth="3" />
          <path d={pathFor(prognoseLijn)} fill="none" stroke={calc.kleur} strokeWidth="2" strokeDasharray="6 3" />
        </svg>
        <div style={{ display: 'flex', gap: 20, fontSize: 12, flexWrap: 'wrap', marginTop: 12 }}>
          <span><span style={{ display: 'inline-block', width: 14, height: 3, background: '#94a3b8', verticalAlign: 'middle' }} /> Doel (lineair)</span>
          <span><span style={{ display: 'inline-block', width: 14, height: 3, background: '#10b981', verticalAlign: 'middle' }} /> Werkelijk afgerond</span>
          <span><span style={{ display: 'inline-block', width: 14, height: 3, background: calc.kleur, verticalAlign: 'middle' }} /> Prognose op basis van capaciteit</span>
        </div>
        {calc.capaciteit_tekort && (
          <div style={{ marginTop: 16, padding: 12, background: '#fef2f2', borderLeft: '3px solid #ef4444', fontSize: 13 }}>
            ⚠️ Capaciteitstekort: je team heeft {Math.round(calc.haalbaar_raw * 10) / 10} onderhouden aan capaciteit,
            maar er zijn er nog {calc.te_gaan} te gaan. Meer monteurs, meer uren, of minder storingen nodig.
          </div>
        )}
      </div>
          <DrukteKalender />
      </div>
  );
}
